import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl, FormattedMessage } from 'react-intl';
import { withFlags } from '../../../flags/Flags';

import {
  Button,
  Card,
  Input,
  InputIcon,
  MediaObject,
  Spinner,
  ToastContainer,
  Tooltip
} from '@salesforce/design-system-react';

import './AttributeList.scss';
import {
  DataExtensionToast,
  ImportProfileWizard,
  NoAttributes,
  ProfileAttributeTable
} from '../';

class ProfileAttributeList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      mode: 'full'
    };
  }
  columns(flags) {
    const cols = {
      name: { sortable: false, width: '300px' },
      hasValues: { sortable: false, width: '200px' },
      valueCount: { sortable: false, width: '150px' },
      dataType: { sortable: false, width: '300px' },
      updatedAt: { sortable: false, width: '215px' }
    };
    if (!flags.isReadonly) {
      cols.actions = { sortable: false };
    }
    return cols;
  }

  maxAttributes() {
    return 30;
  }

  header(items) {
    const count = items ? items.length : 0;
    const maxAttributes = this.maxAttributes();

    return (
      <div className="attribute-header">
        <h2>
          <FormattedMessage id="components.ecs.attributes.profileHeader" />
        </h2>
        <FormattedMessage
          id="components.ecs.attributes.items"
          values={{ count, maxAttributes }}
        />
      </div>
    );
  }

  // TODO: duplicated code in AdvancedSettings
  previousDataExtension() {
    const sourceInfo = this.props?.importConfiguration?.data
      ?.import_configuration?.source_info;
    if (sourceInfo) {
      return {
        ...sourceInfo,
        customer_key: sourceInfo.customer_de_key,
        sendable_data_extension_field: { name: sourceInfo.subscriber_key }
      };
    }
  }

  // TODO: duplicated code in AdvancedSettings
  attributesForWizard() {
    const subscriberKey = this.previousDataExtension()
      ?.sendable_data_extension_field?.name;

    // add id, label, and lock flag for the ui (picklist mostly)
    return this.props.attributes.map((attr, i) => ({
      id: `${attr.name}`,
      label: attr.name,
      locked: attr.name === subscriberKey,
      ...attr
    }));
  }

  // TODO: duplicated code in AdvancedSettings
  openProfileImportWizard(mode) {
    this.setState({ mode });

    const importConfiguration = this.props.importConfiguration?.data
      ?.import_configuration;

    if (!this.props.dataExtensions?.data?.data_extensions) {
      this.props.actions.getDataExtensions();
    }

    if (importConfiguration?.source_info) {
      const sourceInfo = importConfiguration?.source_info;
      const dataExtension = {
        ...sourceInfo,
        customer_key: sourceInfo.customer_de_key,
        sendable_data_extension_field: { name: sourceInfo.subscriber_key }
      };

      const profileAttributes = this.attributesForWizard();

      // Load DE details if we have a previous import
      this.props.actions.getDataExtensionColumns(dataExtension);

      this.props.actions.openModal('profileImport', {
        step1: { dataExtension: dataExtension },
        step2: {
          completed: true,
          schedule: {
            ...importConfiguration.schedule,
            importRecurring: importConfiguration?.schedule?.sync_days > 0
          },
          notification_info: importConfiguration?.notification_info || {}
        },
        step3: { selectedAttributes: profileAttributes },
        step5: { profileAttributes: profileAttributes },
        currentStep: mode === 'full' ? undefined : 'step3'
      });
    } else {
      this.props.actions.openModal('profileImport', {
        step1: { completed: false },
        currentStep: mode === 'full' ? undefined : 'step3'
      });
    }
  }

  importAllowed(flags) {
    const { lastImport } = this.props;
    const importConfigLoading = this.props.importConfiguration?.isLoading;

    if (!this.props.attributes?.length) {
      return false;
    }

    return (
      !importConfigLoading &&
      !(flags && flags.isReadonly) &&
      !(lastImport && ['importing', 'queued'].includes(lastImport.status))
    );
  }

  headerActions(flags) {
    const { formatMessage } = this.props.intl;
    const searchPlaceholder = formatMessage({
      id: 'components.ecs.attributes.searchPlaceholder'
    });
    const importAllowed = this.importAllowed(flags);

    let manageAttributes = (
      <Button
        label={formatMessage({
          id: 'components.ecs.attributes.manageAttributes'
        })}
        onClick={() => this.openProfileImportWizard('short')}
        disabled={!this.importAllowed(flags)}
      />
    );
    if (!importAllowed && this.props.attributes?.length) {
      manageAttributes = (
        <Tooltip
          align="top right"
          content={formatMessage({
            id: 'components.ecs.attributes.manageAttributesTooltip'
          })}
        >
          {manageAttributes}
        </Tooltip>
      );
    }

    return (
      <div className="attribute-header-actions">
        <Input
          className="filter-by-name"
          iconLeft={
            <InputIcon
              assistiveText={{
                icon: 'Search'
              }}
              name="search"
              category="utility"
            />
          }
          onChange={(evt, { value }) =>
            this.props.actions.updateAttributeFilter({ name: value })
          }
          placeholder={searchPlaceholder}
        />
        {manageAttributes}
      </div>
    );
  }

  drawSpinner(isLoading) {
    return isLoading ? (
      <div className="pi-common-spinner">
        <Spinner variant="brand" />
      </div>
    ) : null;
  }

  renderNoData() {
    return (
      <Card
        className="attribute-card"
        header={<MediaObject body={this.header([])} />}
        headerActions={this.headerActions(true)}
      >
        <NoAttributes
          attributeType="profile"
          intl={this.props.intl}
          showGetStarted={true}
          onGetStarted={() => this.openProfileImportWizard('full')}
        />
      </Card>
    );
  }

  renderTable() {
    const { attributes, filter } = this.props;
    const filtered = filter?.name
      ? attributes.filter(item =>
          item.name.toUpperCase().includes(filter.name.toUpperCase())
        )
      : attributes;

    return withFlags(flags => (
      <Card
        className="attribute-card"
        header={<MediaObject body={this.header(filtered)} />}
        headerActions={this.headerActions(flags)}
      >
        <div className="attribute-list-div">
          <ProfileAttributeTable
            attributes={filtered}
            columns={this.columns(flags)}
            isReadonly={flags.isReadonly}
            onListAction={this.props.onListAction}
          />
        </div>
      </Card>
    ));
  }

  render() {
    const { attributes, isLoading } = this.props;

    return withFlags(flags => (
      <React.Fragment>
        {this.drawSpinner(isLoading)}
        {!isLoading && !attributes?.length
          ? this.renderNoData()
          : this.renderTable()}
        <ToastContainer>
          <DataExtensionToast
            dataExtensions={this.props.dataExtensions}
            actions={this.props.actions}
            toast={this.props.toast}
          />
        </ToastContainer>
        <ImportProfileWizard
          actions={this.props.actions}
          dataExtensions={this.props.dataExtensions}
          importConfiguration={this.props.importConfiguration}
          modals={this.props.modals}
          rules={this.props.rules}
          location={this.props.location}
          mode={this.state.mode}
          flags={flags}
          maxAttributes={this.maxAttributes()}
        />
      </React.Fragment>
    ));
  }
}

ProfileAttributeList.propTypes = {
  attributes: PropTypes.array.isRequired,
  dataExtensions: PropTypes.object.isRequired,
  importConfiguration: PropTypes.object.isRequired,
  filter: PropTypes.object,
  isLoading: PropTypes.bool.isRequired,
  isSaving: PropTypes.bool.isRequired,
  lastImport: PropTypes.object,
  lastSave: PropTypes.object,
  onListAction: PropTypes.func.isRequired,
  modals: PropTypes.object.isRequired,
  rules: PropTypes.object.isRequired,
  toast: PropTypes.object,
  location: PropTypes.object,
  intl: intlShape.isRequired
};

export default injectIntl(ProfileAttributeList);
